import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, CheckCircle2, Download, LayoutDashboard, Layers, Users } from "lucide-react";
import { toast } from "sonner";
import { CheckoutSecureNote } from "@/components/PaymentTrust";

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = RZP_SRC; s.onload = () => resolve(true); s.onerror = () => resolve(false);
  document.body.appendChild(s);
});
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const AddonDialog = ({ open, onOpenChange, defaultTab = "account", onPurchased }) => {
  const navigate = useNavigate();
  const [acct, setAcct] = useState([]);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null); // {type, key, price, label}
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSuccess(null); setSel(null);
    api.get("/billing/addons").then((r) => {
      setAcct(r.data.account_addons || []);
      setUsers(r.data.user_addons || []);
    }).catch(() => toast.error("Could not load add-ons"));
  }, [open]);

  const downloadInvoice = async () => {
    if (!success?.paymentId) return;
    try {
      const r = await api.get(`/billing/invoice/${success.paymentId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice_${success.paymentId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download invoice"); }
  };

  const pay = async () => {
    if (!sel) return;
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) { toast.error("Failed to load payment gateway"); setPaying(false); return; }
      const { data } = await api.post("/billing/addon/create-order", { addon_type: sel.type, key: sel.key });
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "RecoverOS", description: `${sel.label} (one-time add-on)`,
        prefill: data.prefill || {}, theme: { color: "#0B3D91" },
        modal: { ondismiss: () => setPaying(false) },
        handler: async (resp) => {
          try {
            await api.post("/billing/addon/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              addon_type: sel.type, key: sel.key,
            });
            window.dispatchEvent(new Event("recoveros:usage-changed"));
            onPurchased?.();
            setSuccess({ label: sel.label, paymentId: resp.razorpay_payment_id });
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment could not be verified");
          } finally { setPaying(false); }
        },
      });
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed"); setPaying(false); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  const Card = ({ type, a }) => {
    const active = sel?.type === type && sel?.key === a.key;
    return (
      <button type="button" onClick={() => setSel({ type, key: a.key, price: a.price, label: a.label })}
        data-testid={`addon-${type}-${a.key}`}
        className={`text-left rounded-sm border p-4 transition-colors ${active ? "border-[#0B3D91] ring-1 ring-[#0B3D91] bg-[#0B3D91]/5" : "border-border hover:border-[#0B3D91]/40"}`}>
        <div className="flex items-center justify-between">
          <span className="font-heading font-bold text-sm">{a.label}</span>
          {active && <Check className="h-4 w-4 text-[#16A34A]" />}
        </div>
        <div className="mt-1 text-xl font-bold">{fmtINR(a.price)}<span className="text-xs font-normal text-muted-foreground">/mo</span></div>
        <div className="mt-1 text-[11px] text-muted-foreground">One-time capacity boost · activates instantly</div>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="addon-dialog">
        {success ? (
          <div className="text-center py-4" data-testid="addon-success">
            <div className="h-16 w-16 rounded-full bg-[#16A34A]/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-9 w-9 text-[#16A34A]" />
            </div>
            <h2 className="font-heading font-bold text-2xl mt-4">Congratulations!</h2>
            <p className="text-muted-foreground mt-1">Your purchase of <strong>{success.label}</strong> is activated successfully. Your new limits are available immediately.</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outline" className="rounded-sm gap-1.5" onClick={downloadInvoice} data-testid="addon-success-invoice-btn">
                <Download className="h-4 w-4" /> GST Invoice
              </Button>
              <Button className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" onClick={() => { onOpenChange(false); navigate("/dashboard"); }} data-testid="addon-success-dashboard-btn">
                <LayoutDashboard className="h-4 w-4" /> Go To Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Buy add-ons</DialogTitle>
              <DialogDescription>Increase your account or user limits instantly — no plan change required.</DialogDescription>
            </DialogHeader>

            <div className="mt-1">
              <div className="flex items-center gap-1.5 label-caps mb-2"><Layers className="h-3.5 w-3.5" /> Account Add-ons</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {acct.map((a) => <Card key={a.key} type="account" a={a} />)}
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center gap-1.5 label-caps mb-2"><Users className="h-3.5 w-3.5" /> User Add-ons</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {users.map((a) => <Card key={a.key} type="user" a={a} />)}
              </div>
            </div>

            <Button onClick={pay} disabled={paying || !sel}
              className="mt-4 w-full rounded-sm bg-[#16A34A] hover:bg-[#138a3e] gap-1.5" data-testid="addon-pay-btn">
              {paying ? "Processing…" : <>Pay {sel ? fmtINR(sel.price) : ""} & Activate <ArrowRight className="h-4 w-4" /></>}
            </Button>
            <CheckoutSecureNote className="mt-3" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
